/* eslint-disable consistent-return */
import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from 'rebass/styled-components';
import styled from 'styled-components';
import theme from 'styled-theming';
import {
  AutomationItem,
} from '.';
import api from '../api';
import AutomationListMenu from './AutomationListMenu';

const automationTypes = {
  BOT: 'bot',
  KB: 'kb',
};

const textColor = theme('mode', {
  light: (props) => props.theme.colors.deepGrey,
  dark: (props) => props.theme.colors.greyMiddle,
});

const scrollThumbBG = theme('mode', {
  light: (props) => props.theme.colors.greyLight,
  dark: (props) => props.theme.colors.paynesGrey,
});

const scrollBG = theme('mode', {
  light: (props) => props.theme.colors.auroMetalSaurus,
  dark: (props) => props.theme.colors.auroMetalSaurus,
});

const AutomationListStyled = styled(Flex)`
  max-width: 914px;
  max-height: 358px;
  overflow-y: auto;
  padding-right: 6px;
  width: calc(100% - 20px);
  &::-webkit-scrollbar {
    background-color: transparent;
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${scrollThumbBG};
    outline: none;
    border-radius: 8px;

    &:hover {
      background-color: ${scrollBG};
    }
  }
`;

const ScrollStyled = styled(Flex)`
  background-color: rgba(255, 255, 255, 0.06);
  margin-left: -14px;
  width: 20px;
`;

function AutomationList(props) {
  const [isSavingAutomation, setIsSavingAutomation] = useState([]);
  const [skillsList, setSkillsList] = useState([]);
  const [automationsInfo, setAutomationsInfo] = useState([]);
  const [scrollbarBackground, setScrollbarBackground] = useState(false);
  const isMounted = useRef(true);
  const containerRef = useRef(null);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedAutomations, setSelectedAutomations] = useState([]);

  const {
    automations,
    themeMode,
    getAutomations,
    addNotification,
    brandId,
    automationType,
  } = props;

  /**
   * Get updating method for KB or Bot
   */
  function getUpdateMethod() {
    return (automationType === automationTypes.KB
      ? api.automationLookup.updateKBV2
      : api.automationLookup.updateAutomationV2).bind(api.automationLookup);
  }

  function addSavingAutomations(id) {
    if (isSavingAutomation.includes(id)) {
      return;
    }
    setIsSavingAutomation([
      ...isSavingAutomation,
      id,
    ]);
  }

  function removeSavingAutomations(id) {
    setIsSavingAutomation(isSavingAutomation.filter((item) => item !== id));
  }

  function getAutomationId(automation) {
    return automationType === automationTypes.KB
      ? automation.knowledgeBaseId
      : automation.botId;
  }

  function getAutomationName(automation) {
    return automation.botName || automation.name;
  }

  function notifyAutomationUpdate(automation, error) {
    if (!isMounted.current) {
      return;
    }
    const name = getAutomationName(automation);
    if (error) {
      addNotification(`Error updating automation "${name}" - ${error.message}`, 'error');
    } else {
      addNotification(`Automation "${name}" updated successfully`, 'success');
    }
  }

  function notifyCredentialsUpdate(automation, error) {
    if (!isMounted.current) {
      return;
    }
    const name = getAutomationName(automation);
    if (error) {
      addNotification(`Credentials for ${name} were not updated - ${error.message}`, 'error');
    } else {
      addNotification(`Credentials for ${name} were updated successfully`, 'success');
    }
  }

  async function deleteAction(id) {
    if (isSavingAutomation.length) {
      return;
    }
    addSavingAutomations(id);
    try {
      if (automationType === automationTypes.KB) {
        await api.automationLookup.deleteAutomationV2(brandId, id);
      } else {
        await api.automationLookup.deleteAutomationV2(brandId, id);
      }
      if (isMounted.current) {
        const updatedAutomations = automationsInfo
          .filter((originalAutomation) => (getAutomationId(originalAutomation) !== id));
        setAutomationsInfo(updatedAutomations);
        addNotification('Automation deleted successfully', 'success');
      }
    } catch (error) {
      if (isMounted.current) {
        addNotification(error, 'error');
      }
    }
    if (isMounted.current) {
      removeSavingAutomations(id);
    }
  }

  async function updateAction(automation) {
    if (isSavingAutomation.length) {
      return;
    }
    const id = getAutomationId(automation);
    addSavingAutomations(id);
    try {
      const updateMethod = getUpdateMethod();
      await updateMethod(brandId, automation);
      if (isMounted.current) {
        const updatedAutomations = automationsInfo
          .map((orig) => (getAutomationId(orig) === id
            ? { ...automation } : orig));
        setAutomationsInfo(updatedAutomations);
        notifyAutomationUpdate(automation);
      }
    } catch (error) {
      notifyAutomationUpdate(automation, error);
    }
    if (isMounted.current) {
      removeSavingAutomations(id);
    }
  }

  /**
   * Automation was selected with checkbox
   */
  function onSelectChangeHandler(automation) {
    const updatedAutomations = automationsInfo
      .map((orig) => (getAutomationId(orig) === getAutomationId(automation)
        ? { ...automation }
        : orig
      ));
    setAutomationsInfo(updatedAutomations);
  }

  /**
   * All automations were selected by Select all checkbox
   */
  function selectAllChangeHandler() {
    const updatedAutomations = automationsInfo.map((automation) => ({
      ...automation,
      checked: !selectAll,
    }));
    setAutomationsInfo(updatedAutomations);
  }

  function getSelectedAutomationsIds() {
    return selectedAutomations.map((automation) => getAutomationId(automation));
  }

  /**
   * Update bot credentials
   */
  async function updateAutomationCredentials() {
    if (isSavingAutomation.length) {
      return;
    }
    const selectedIds = getSelectedAutomationsIds();
    setIsSavingAutomation(selectedIds);
    try {
      await api.automationLookup.updateAutomationCredsV2(brandId, selectedIds);
      selectedAutomations.forEach((automation) => notifyCredentialsUpdate(automation));
    } catch (error) {
      selectedAutomations.forEach((automation) => notifyCredentialsUpdate(automation, error));
    } finally {
      setIsSavingAutomation([]);
    }
  }

  function getUpdatedAutomations(updates) {
    return automationsInfo.map((item) => {
      const foundItem = selectedAutomations
        .find((selectedItem) => getAutomationId(item) === getAutomationId(selectedItem));

      if (foundItem) {
        return {
          ...item,
          ...updates,
        };
      }
      return item;
    });
  }

  /**
   * Enable/Disable selected items
   * @param {*} value:boolean, is the item enabled or not
   */
  async function changeAutomationsStatusHandler(value) {
    if (isSavingAutomation.length) {
      return;
    }
    setIsSavingAutomation(getSelectedAutomationsIds());

    try {
      const updateMethod = getUpdateMethod();
      await Promise.all(selectedAutomations.map((item) => updateMethod(brandId, {
        ...item,
        enabled: value,
      })));

      const updatedAutomations = getUpdatedAutomations({
        enabled: value,
      });
      setAutomationsInfo(updatedAutomations);

      selectedAutomations.forEach((automation) => notifyAutomationUpdate(automation));
    } catch (error) {
      selectedAutomations.forEach((automation) => notifyAutomationUpdate(automation, error));
      // refetch automations for case some of them were updated
      await getAutomations();
    } finally {
      setIsSavingAutomation([]);
    }
  }

  /**
   * Add or remove all skills for selected items
   * @param {*} value true for adding and false for removing skills
   */
  async function updateAllSkillsHandler(value) {
    if (isSavingAutomation.length) {
      return;
    }
    setIsSavingAutomation(getSelectedAutomationsIds());

    try {
      const updateMethod = getUpdateMethod();
      const enabledSkillIds = value
        ? skillsList.map(({ id }) => String(id))
        : [];
      await Promise.all(selectedAutomations.map((item) => updateMethod(brandId, {
        ...item,
        enabledSkillIds,
      })));

      const updatedAutomations = getUpdatedAutomations({
        enabledSkillIds,
      });
      setAutomationsInfo(updatedAutomations);

      selectedAutomations.forEach((automation) => notifyAutomationUpdate(automation));
    } catch (error) {
      selectedAutomations.forEach((automation) => notifyAutomationUpdate(automation, error));
      // refetch automations for case some of them were updated
      await getAutomations();
    } finally {
      setIsSavingAutomation([]);
    }
  }

  useEffect(() => {
    async function fetchSkills() {
      const skills = await api.automationLookup.getSkills(brandId);
      const skillsMapped = skills.map((skill) => ({
        id: skill.id,
        label: skill.name,
        value: skill.name,
      }));
      setSkillsList(skillsMapped);
      setIsSavingAutomation([]);
    }
    fetchSkills();
    setAutomationsInfo(automations.map((automation) => ({
      ...automation,
      checked: false,
    })));
  }, [automations, brandId]);

  useEffect(() => {
    if (containerRef.current && isMounted.current) {
      setScrollbarBackground(containerRef.current.scrollHeight > containerRef.current.offsetHeight);
    }
  }, [skillsList]);

  useEffect(() => {
    if (isMounted.current) {
      setSelectAll(automationsInfo.every((automation) => automation.checked));
    }
    setSelectedAutomations(automationsInfo.filter((automation) => automation.checked));
  }, [automationsInfo]);

  return (
    <Box data-testid="automation-list">
      <AutomationListMenu
        selectedAll={selectAll}
        selectedAutomations={selectedAutomations}
        automationType={automationType}
        onSelectAllChange={selectAllChangeHandler}
        onUpdateAutomationCredentials={updateAutomationCredentials}
        onEnableAutomations={changeAutomationsStatusHandler}
        onRemoveSkills={() => updateAllSkillsHandler(false)}
        onAddSkills={() => updateAllSkillsHandler(true)}
      />

      <Flex flexDirection="row" alignItems="left">
        <AutomationListStyled
          flexDirection="column"
          alignItems="left"
          ref={containerRef}
        >
          {automationsInfo.map((automation) => (
            <AutomationItem
              automation={automation}
              key={getAutomationId(automation)}
              themeMode={themeMode}
              addNotification={addNotification}
              brandId={brandId}
              deleteAction={() => deleteAction(getAutomationId(automation))}
              updateAction={(updatedAutomation) => updateAction(updatedAutomation)}
              onSelect={onSelectChangeHandler}
              skillsList={skillsList}
              isSavingAutomation={isSavingAutomation}
            />
          ))}
        </AutomationListStyled>
        {scrollbarBackground && <ScrollStyled />}
      </Flex>
    </Box>
  );
}

AutomationList.defaultProps = {
  automationType: 'bot',
};
AutomationList.propTypes = {
  automations: PropTypes.array.isRequired,
  themeMode: PropTypes.string.isRequired,
  getAutomations: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  brandId: PropTypes.string.isRequired,
  automationType: PropTypes.string,
};

export default AutomationList;
